// OrpheusConsentApi — the account's Orpheus switch. GET reads, POST grants, DELETE revokes, and all
// three answer the full status, so the screen never re-reads after a write. Grant/revoke is
// account-admin only; GET is open to anyone with access in the account.
//
// Bare JSON, not enveloped — read `data` directly. `suppressGlobalErrorHandler`: the settings pane
// renders every failure in its own words.
import type { OrpheusConsentStatusDto, OrpheusConsentVariant } from '../models/orpheusConsent.types'

import { appHttp } from '@/shared/http'

const consentUrl = (accountId: number | string): string => `/accounts/${accountId}/orpheus-consent/`

/** The canonical feature-chat ask door; the probe below never carries an account segment. */
const askUrl = (workspaceId: number | string): string =>
  `/api/chatbot/workspaces/${encodeURIComponent(String(workspaceId))}/feature-chat/ask`

export class OrpheusConsentApi {
  // Injectable for tests; defaults to the singleton instance.
  constructor(private readonly http: typeof appHttp = appHttp) {}

  private readonly cfg = { suppressGlobalErrorHandler: true } as const

  async fetch(accountId: number | string): Promise<OrpheusConsentStatusDto> {
    return (await this.http.get<OrpheusConsentStatusDto>(consentUrl(accountId), this.cfg)).data
  }

  async grant(
    accountId: number | string,
    variant: OrpheusConsentVariant = 'orphex_credits',
  ): Promise<OrpheusConsentStatusDto> {
    return (
      await this.http.post<OrpheusConsentStatusDto>(consentUrl(accountId), { variant }, this.cfg)
    ).data
  }

  async revoke(accountId: number | string): Promise<OrpheusConsentStatusDto> {
    return (await this.http.delete<OrpheusConsentStatusDto>(consentUrl(accountId), this.cfg)).data
  }

  /**
   * For callers the status read refuses (plain members): an empty-body ask, which the backend
   * answers with its consent verdict before it validates anything — it never reaches the model and
   * is never charged. Always rejects; `readConsentProbe` reads the rejection. A 2xx (never seen in
   * practice) means consent passed.
   */
  async probe(workspaceId: number | string): Promise<void> {
    await this.http.post(askUrl(workspaceId), {}, this.cfg)
  }
}
